/**
 * TradingView Advanced Chart Component
 * Wrapper for TradingView Charting Library with full API access
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { LottieLoader } from "@/components/lottie-loader";

// localStorage key for persisting user's selected chart interval
const CHART_INTERVAL_KEY = "nordex_chart_interval";
import type { Theme } from "@/theme/theme-context";
import { backendDatafeed } from "./datafeed/backend-datafeed";
import { PositionOverlay, type TPSLModifyCallback, type PartialTarget } from "./overlays/position-overlay";
import { OrdersOverlay, type OrderModifyCallback } from "./overlays/orders-overlay";
// import { HistoryOverlay } from "./overlays/history-overlay"; // DISABLED: Buy/Sell signals removed
import { TPSLEditModal } from "./tpsl-edit-modal";
import { useAccountStore, type StorePosition } from "@/backend/store";
import { useBackendOrders } from "@/backend/hooks/use-backend-orders";
// import { useTradeHistory } from "@/backend/hooks/use-trade-history"; // DISABLED: Buy/Sell signals removed
import { useUpdateTPSL } from "@/backend/hooks/use-update-tpsl";
import { usePrivyAuth } from "@/backend/use-privy-auth";
import { OrdersApi, PositionsApi } from "@/backend/api-client";
import { toBackendOrderRequest } from "@/backend/types";
import { useTradingSettings } from "@/features/trading-settings/trading-settings-context";
import { useI18n } from "@/features/i18n";
import { toast } from "sonner";

interface TradingViewAdvancedChartProps {
  productId: string; // This is the Binance symbol (e.g., "BTCUSDT")
  theme: Theme;
  isMobile?: boolean;
  onChartReady?: (chart: TradingView.IChartWidgetApi) => void;
}

// Map Binance symbols back to market IDs for position lookup
const binanceToMarketMap: Record<string, string> = {
  "BTCUSDT": "BTC-USD",
  "ETHUSDT": "ETH-USD",
  "SOLUSDT": "SOL-USD",
  "BNBUSDT": "BNB-USD",
  "XRPUSDT": "XRP-USD",
  "ADAUSDT": "ADA-USD",
  "DOGEUSDT": "DOGE-USD",
  "POLUSDT": "MATIC-USD",
  "DOTUSDT": "DOT-USD",
  "LTCUSDT": "LTC-USD",
};

export const TradingViewAdvancedChart = ({
  productId,
  theme,
  isMobile = false,
  onChartReady,
}: TradingViewAdvancedChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<TradingView.IChartingLibraryWidget | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const positionOverlayRef = useRef<PositionOverlay>(new PositionOverlay());
  const ordersOverlayRef = useRef<OrdersOverlay>(new OrdersOverlay());
  // Ref to store TradingView subscription cleanup (onIntervalChanged, etc.)
  const tvSubscriptionCleanupRef = useRef<(() => void) | null>(null);
  // const historyOverlayRef = useRef<HistoryOverlay>(new HistoryOverlay()); // DISABLED: Buy/Sell signals removed
  
  // Mobile modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPosition] = useState<StorePosition | null>(null);

  // Convert Binance symbol to market ID for position lookup
  const marketId = binanceToMarketMap[productId] || productId;

  // Get positions from account store
  const positions = useAccountStore((state) => state.positions);
  
  // Get user ID
  const { userId: userIdFromPrivy } = usePrivyAuth();
  const userId = userIdFromPrivy ?? undefined;
  
  // Get orders
  const { openOrders } = useBackendOrders(userId);
  
  // Get trade history - DISABLED: Buy/Sell signals removed
  // const { data: fills = [] } = useTradeHistory(userId);
  
  // TP/SL update mutation
  const updateTPSLMutation = useUpdateTPSL(userId);
  
  // Get leverage settings for order modification
  const { getLeverage } = useTradingSettings();
  
  // Get user's timezone preference for chart display
  const { timezone } = useI18n();
  
  // Track if we're currently modifying an order (to prevent duplicate modifications)
  const isModifyingRef = useRef<Set<string>>(new Set());
  
  // Order modification callback - called when user drags an order line to a new price
  const handleOrderModify: OrderModifyCallback = useCallback(async (orderId, newPrice, order) => {
    // Prevent duplicate modifications
    if (isModifyingRef.current.has(orderId)) {
      return;
    }
    
    // Mark as modifying
    isModifyingRef.current.add(orderId);
    
    try {
      const oldPrice = order.price;
      const priceChange = ((newPrice - oldPrice) / oldPrice * 100).toFixed(2);
      const sideLabel = order.side === "buy" ? "BUY" : "SELL";
      const formattedPrice = newPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      
      console.log(`[TradingView] Modifying ${sideLabel} order ${orderId}: $${oldPrice.toFixed(2)} → $${formattedPrice} (${priceChange}%)`);
      
      // Show optimistic toast with side label
      const toastId = toast.loading(`Setting ${sideLabel} limit to $${formattedPrice}...`);
      
      // Step 1: Cancel the old order
      await OrdersApi.cancelOrder(orderId);
      
      // Step 2: Create new order with same params but new price
      const side = order.side === "buy" ? "long" : "short";
      const leverage = getLeverage(order.market);
      
      const request = toBackendOrderRequest(
        order.market,
        side,
        order.size,
        newPrice,
        "limit",
        { leverage, timeInForce: "GTC" }
      );
      
      await OrdersApi.placeOrder(request);
      
      toast.success(`${sideLabel} limit set to $${formattedPrice}`, { id: toastId });
      
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Failed to modify order";
      console.error("[TradingView] Order modify error:", errorMsg);
      toast.error(`Failed to modify order: ${errorMsg}`);
    } finally {
      // Allow future modifications
      isModifyingRef.current.delete(orderId);
    }
  }, [getLeverage]);
  
  // Set up order modification callback
  useEffect(() => {
    ordersOverlayRef.current.setOnModifyCallback(handleOrderModify);
    
    return () => {
      ordersOverlayRef.current.setOnModifyCallback(null);
    };
  }, [handleOrderModify]);
  
  // TP/SL modification callback - called when user drags a TP or SL line to a new price
  const handleTPSLModify: TPSLModifyCallback = useCallback(async (type, newPrice, position) => {
    if (!userId) return;
    
    const overlay = positionOverlayRef.current;
    overlay.setModifying(type, true);
    
    try {
      const typeLabel = type === "tp" ? "Take Profit" : "Stop Loss";
      const formattedPrice = newPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      
      console.log(`[TradingView] Modifying ${typeLabel}: → $${formattedPrice}`);
      
      // Show optimistic toast
      const toastId = toast.loading(`Setting ${typeLabel} to $${formattedPrice}...`);
      
      // Call the backend to update TP/SL using PositionsApi (with proper auth)
      const newTp = type === "tp" ? newPrice : (position.takeProfit ?? null);
      const newSl = type === "sl" ? newPrice : (position.stopLoss ?? null);
      
      await PositionsApi.updateTpSl(marketId, newTp, newSl);
      
      toast.success(`${typeLabel} set to $${formattedPrice}`, { id: toastId });
      
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : `Failed to update ${type === "tp" ? "Take Profit" : "Stop Loss"}`;
      console.error("[TradingView] TP/SL modify error:", errorMsg);
      toast.error(errorMsg, { id: undefined }); // New toast, don't replace loading
    } finally {
      overlay.setModifying(type, false);
    }
  }, [userId, marketId]);
  
  // Set up TP/SL modification callback
  useEffect(() => {
    positionOverlayRef.current.setOnModifyCallback(handleTPSLModify);
    
    return () => {
      positionOverlayRef.current.setOnModifyCallback(null);
    };
  }, [handleTPSLModify]);


  // Initialize TradingView widget
  useEffect(() => {
    if (!containerRef.current || !window.TradingView) {
      console.error("[TradingViewChart] TradingView library not loaded");
      setHasError(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsReady(false);  // Reset ready state so overlays wait for new chart
    setHasError(false);

    // Clean up previous widget
    if (widgetRef.current) {
      widgetRef.current.remove();
      widgetRef.current = null;
    }

    // Create new widget immediately - try-catch blocks in callbacks handle race conditions
    try {
      // Get saved interval from localStorage, default to "1D"
      const savedInterval = localStorage.getItem(CHART_INTERVAL_KEY) || "1D";
      
      const widget = new window.TradingView.widget({
        container: containerRef.current,
        library_path: "/tradingview/advanced_charts/charting_library/",
        datafeed: backendDatafeed,
        symbol: `BINANCE:${productId}`,
        interval: savedInterval as TradingView.ResolutionString,
        locale: "en",
        // User's timezone preference from I18n context
        timezone: timezone as TradingView.Timezone,
        // theme will be applied in onChartReady via changeTheme() to ensure proper order
        autosize: true,
        fullscreen: false,
        
        // Quick-select interval buttons (like GMX): 1m | 5m | 15m | 1h | 4h | D | W | M
        favorites: {
          intervals: ["1", "5", "15", "60", "240", "1D", "1W", "1M"] as TradingView.ResolutionString[],
        },
        // Use full-sized buttons for intervals when there's space
        header_widget_buttons_mode: "fullsize",
        
        disabled_features: [
          "use_localstorage_for_settings",  // We use our own custom interval persistence
          "save_chart_properties_to_local_storage",  // We use our own custom interval persistence
          "header_symbol_search",
          "symbol_search_hot_key",
          "header_compare",
          // "header_screenshot",  // Enabled - shows snapshot icon
          // "header_chart_type",  // Enabled - shows candles/bars dropdown  
          "header_settings",
          "header_undo_redo",  // Hide left/right arrow buttons
          "header_quick_search",  // Hide quick search icon
          "header_fullscreen_button",  // Hide fullscreen icon (we have our own)
          "timeframes_toolbar",  // Hide bottom toolbar (5y, 1y, 3m, 5d, 1d, timezone, %, log, auto)
        ],
        enabled_features: [
          "items_favoriting",  // Allow users to customize their favorite intervals
        ],
        // settings_overrides FORCES these settings (cannot be overridden by saved preferences)
        // This ensures the gradient background and status line settings apply immediately
        settings_overrides: {
          // Canvas: Gradient background (#1c1c1c to #191919)
          "paneProperties.background": "#1c1c1c",
          "paneProperties.backgroundType": "gradient",
          "paneProperties.backgroundGradientStartColor": "#1c1c1c",
          "paneProperties.backgroundGradientEndColor": "#191919",
          
          // Grid lines with colors AND style (40% opacity for subtle transparency)
          "paneProperties.vertGridProperties.color": "rgba(38, 38, 38, 0.4)",
          "paneProperties.horzGridProperties.color": "rgba(38, 38, 38, 0.4)",
          "paneProperties.vertGridProperties.style": 0,
          "paneProperties.horzGridProperties.style": 0,
          
          // Scales styling
          "scalesProperties.backgroundColor": "#1a1a1a",
          "scalesProperties.lineColor": "#2d2d2d",
          "scalesProperties.textColor": "#8a8a90",
          "scalesProperties.fontSize": 11,
          
          // Status Line: Show Ticker, Chart values, Bar change, Open market status; Hide Volume
          "paneProperties.legendProperties.showSeriesTitle": true,  // Ticker symbol: ON
          "paneProperties.legendProperties.showSeriesOHLC": true,  // Chart values: ON
          "paneProperties.legendProperties.showBarChange": true,   // Bar change values: ON
          "paneProperties.legendProperties.showVolume": false,     // Volume: OFF
          
          // Indicators: Titles, Inputs, Values all OFF
          "scalesProperties.showStudyLastValue": false,
          // Show current price label on axis
          "scalesProperties.showSeriesLastValue": true,
          
          // Candle colors - all parts aligned to site theme
          // Up candles (green: #10b981)
          "mainSeriesProperties.candleStyle.upColor": "#10b981",
          "mainSeriesProperties.candleStyle.borderUpColor": "#10b981",
          "mainSeriesProperties.candleStyle.wickUpColor": "#10b981",
          // Down candles (red: #f6465d)
          "mainSeriesProperties.candleStyle.downColor": "#f6465d",
          "mainSeriesProperties.candleStyle.borderDownColor": "#f6465d",
          "mainSeriesProperties.candleStyle.wickDownColor": "#f6465d",
        },
        overrides: {
          // Empty - all styling is now in settings_overrides for higher precedence
        },
        studies_overrides: {
          // Volume indicator colors - match site colors
          "volume.volume.color.0": "#f6465d",  // Down volume (red) - matches site red
          "volume.volume.color.1": "#10b981",  // Up volume (green)
        },
        loading_screen: {
          backgroundColor: "#1a1a1a",
        },
        // Custom CSS relative to library_path for proper theming
        custom_css_url: "../../nordex-theme.css",
      });

      widgetRef.current = widget;

      // Wait for chart to be ready
      widget.onChartReady(() => {
        // Guard: Only proceed if this widget is still the current one
        // (prevents old widget callbacks from firing after widget was removed)
        if (widgetRef.current !== widget) {
          return;
        }
        
        setIsReady(true);
        setIsLoading(false);
        
        let chart;
        try {
          chart = widget.activeChart();
        } catch (error) {
          console.error("[TradingViewChart] Error getting chart:", error);
          setHasError(true);
          setIsLoading(false);
          return;
        }
        if (!chart) return;
        
        // Apply theme first, THEN apply our overrides to ensure they stick
        // This is critical because the theme can override our settings
        widget.changeTheme(theme === "dark" ? "dark" : "light").then(() => {
          // Use a small delay to ensure theme has fully loaded before applying overrides
          setTimeout(() => {
            // Now force apply ALL our settings AFTER the theme has loaded
            widget.applyOverrides({
              // Canvas: Gradient background
              "paneProperties.background": "#1c1c1c",
              "paneProperties.backgroundType": "gradient",
              "paneProperties.backgroundGradientStartColor": "#1c1c1c",
              "paneProperties.backgroundGradientEndColor": "#191919",
              
              // Grid lines (40% opacity for subtle transparency)
              "paneProperties.vertGridProperties.color": "rgba(38, 38, 38, 0.4)",
              "paneProperties.horzGridProperties.color": "rgba(38, 38, 38, 0.4)",
              "paneProperties.vertGridProperties.style": 0,
              "paneProperties.horzGridProperties.style": 0,
              
              // Scales
              "scalesProperties.backgroundColor": "#1a1a1a",
              "scalesProperties.lineColor": "#2d2d2d",
              "scalesProperties.textColor": "#8a8a90",
              "scalesProperties.fontSize": 11,
              
              // Status line: Show Ticker, Chart values, Bar change; Hide Volume
              "paneProperties.legendProperties.showSeriesTitle": true,  // Ticker symbol: ON
              "paneProperties.legendProperties.showSeriesOHLC": true,  // Chart values: ON
              "paneProperties.legendProperties.showBarChange": true,   // Bar change values: ON
              "paneProperties.legendProperties.showVolume": false,     // Volume: OFF
              "scalesProperties.showStudyLastValue": false,
              "scalesProperties.showSeriesLastValue": true,  // Show current price label on axis
              
              // Border colors control OHLC status line colors
              "mainSeriesProperties.candleStyle.borderUpColor": "#10b981",
              "mainSeriesProperties.candleStyle.borderDownColor": "#f6465d",
            });
          }, 100); // Small delay to ensure theme loads completely
        });
        
        if (onChartReady && chart) {
          onChartReady(chart);
        }
        
        // Subscribe to interval changes and persist to localStorage
        const intervalChanged = chart.onIntervalChanged();
        const handleIntervalChange = (interval: string) => {
          localStorage.setItem(CHART_INTERVAL_KEY, interval);
        };
        intervalChanged.subscribe(null, handleIntervalChange);
        
        // Store cleanup function so we can unsubscribe when widget is destroyed
        tvSubscriptionCleanupRef.current = () => {
          try {
            intervalChanged.unsubscribeAll(null);
          } catch {
            // Widget may already be disposed, safe to ignore
          }
        };
        });
    } catch (error) {
      console.error("[TradingViewChart] Error initializing widget:", error);
      setHasError(true);
      setIsLoading(false);
    }

    return () => {
      // Clean up TradingView event subscriptions BEFORE removing widget
      // widget.remove() does NOT clean up subscriptions created via .subscribe()
      if (tvSubscriptionCleanupRef.current) {
        tvSubscriptionCleanupRef.current();
        tvSubscriptionCleanupRef.current = null;
      }
      
      // Clean up overlays
      positionOverlayRef.current.clear();
      ordersOverlayRef.current.clear();
      // historyOverlayRef.current.clear(); // DISABLED: Buy/Sell signals removed
      
      if (widgetRef.current) {
        widgetRef.current.remove();
        widgetRef.current = null;
      }
    };
  }, [productId]); // Re-initialize when product changes

  // Update chart timezone when user changes their timezone preference
  useEffect(() => {
    if (!isReady || !widgetRef.current) return;

    try {
      const chart = widgetRef.current.activeChart();
      if (chart) {
        // Use the TradingView Timezone API to update the chart's timezone
        chart.getTimezoneApi().setTimezone(timezone);
      }
    } catch (error) {
      console.warn("[TradingViewChart] Failed to update timezone:", error);
    }
  }, [timezone, isReady]);

  // Update position overlay when position changes
  useEffect(() => {
    if (!isReady || !widgetRef.current) return;

    let chart;
    try {
      chart = widgetRef.current.activeChart();
    } catch (_error) {
      return;
    }
    if (!chart) return;
    
    const position = positions.get(marketId); // Use marketId, not productId!

    const updatePosition = async () => {
      try {
        if (position && position.size > 0) {
          // Position exists and is open - convert StorePosition to Position format
          const positionData: any = {
            ...position,
            collateral: 0, // Not used in overlay, can be 0
          };
          
          // Fetch partial targets if no standard TP/SL is set
          let partialTargets: PartialTarget[] | undefined;
          const hasStandardTpSl = (position.takeProfit && position.takeProfit > 0) || 
                                  (position.stopLoss && position.stopLoss > 0);
          
          if (!hasStandardTpSl) {
            try {
              const targets = await PositionsApi.getTpSlTargets(marketId);
              const activeTargets = targets.filter(t => t.status === 'active');
              if (activeTargets.length > 0) {
                partialTargets = activeTargets.map(t => ({
                  id: t.id,
                  targetType: t.target_type,
                  triggerPrice: parseFloat(t.trigger_price),
                  closeAmount: parseFloat(t.close_amount),
                  closeAmountType: t.close_amount_type,
                  closeAmountValue: parseFloat(t.close_amount_value),
                }));
              }
            } catch (err) {
              // Ignore errors - just don't show partial targets
              console.debug("[TradingViewChart] Failed to fetch partial targets:", err);
            }
          }
          
          // Update position with callback for TP/SL changes and partial targets
          await positionOverlayRef.current.updatePosition(positionData, chart, {
            onTPSLChange: (takeProfit: number | null, stopLoss: number | null) => {
              if (!userId) return;
              updateTPSLMutation.mutate({
                user_id: userId,
                market: marketId,
                take_profit: takeProfit !== null ? takeProfit : undefined,
                stop_loss: stopLoss !== null ? stopLoss : undefined,
              });
            },
          }, partialTargets);
        } else {
          // No position or position is closed
          positionOverlayRef.current.clear();
        }
      } catch (error) {
        console.error("[TradingViewChart] ❌ ERROR creating position overlay:", error);
      }
    };

    updatePosition();
  }, [positions, marketId, isReady]);

  // Update orders overlay when orders change
  useEffect(() => {
    if (!isReady || !widgetRef.current) return;

    let chart;
    try {
      chart = widgetRef.current.activeChart();
    } catch (_error) {
      return;
    }
    if (!chart) return;
    
    const updateOrders = async () => {
      // Convert openOrders to StoreOrder format
      const storeOrders: any[] = openOrders.map(o => ({
        ...o,
        createdAt: new Date(0),
        updatedAt: new Date(0),
        timestamp: 0,
      }));
      await ordersOverlayRef.current.updateOrders(storeOrders, marketId, chart); // Use marketId
    };

    updateOrders();
  }, [openOrders, marketId, isReady]);

  // Update history overlay when fills change
  // DISABLED: Buy/Sell signals were too confusing for users
  // useEffect(() => {
  //   if (!isReady || !widgetRef.current) return;

  //   const chart = widgetRef.current.activeChart();
    
  //   const updateHistory = async () => {
  //     await historyOverlayRef.current.updateHistory(fills, marketId, chart); // Use marketId
  //   };

  //   updateHistory();
  // }, [fills, marketId, isReady]);

  // TP/SL drag handlers are now handled directly in PositionOverlay via Trading Platform API

  // Update theme when it changes
  useEffect(() => {
    if (widgetRef.current && isReady) {
      widgetRef.current.changeTheme(theme === "dark" ? "dark" : "light").then(() => {
        // Re-apply correct background AFTER theme change (100ms delay pattern)
        setTimeout(() => {
          if (theme === "dark") {
            // Apply dark theme gradient background
            widgetRef.current?.applyOverrides({
              "paneProperties.background": "#1c1c1c",
              "paneProperties.backgroundType": "gradient",
              "paneProperties.backgroundGradientStartColor": "#1c1c1c",
              "paneProperties.backgroundGradientEndColor": "#191919",
              "paneProperties.vertGridProperties.color": "rgba(38, 38, 38, 0.4)",
              "paneProperties.horzGridProperties.color": "rgba(38, 38, 38, 0.4)",
              "paneProperties.vertGridProperties.style": 0,
              "paneProperties.horzGridProperties.style": 0,
              "scalesProperties.backgroundColor": "#1a1a1a",
              "scalesProperties.lineColor": "#2d2d2d",
              "scalesProperties.textColor": "#8a8a90",
              "scalesProperties.fontSize": 11,
              "paneProperties.legendProperties.showSeriesTitle": true,  // Ticker symbol: ON
              "paneProperties.legendProperties.showSeriesOHLC": true,
              "paneProperties.legendProperties.showBarChange": true,
              "paneProperties.legendProperties.showVolume": false,
              "scalesProperties.showStudyLastValue": false,
              "scalesProperties.showSeriesLastValue": true,  // Show current price label on axis
              
              // Border colors control OHLC status line colors
              "mainSeriesProperties.candleStyle.borderUpColor": "#10b981",
              "mainSeriesProperties.candleStyle.borderDownColor": "#f6465d",
            });
          } else {
            // Apply light theme white background
            widgetRef.current?.applyOverrides({
              "paneProperties.background": "#ffffff",
              "paneProperties.backgroundType": "solid",
              "paneProperties.vertGridProperties.color": "rgba(229, 229, 229, 0.5)",
              "paneProperties.horzGridProperties.color": "rgba(229, 229, 229, 0.5)",
              "paneProperties.vertGridProperties.style": 0,
              "paneProperties.horzGridProperties.style": 0,
              "scalesProperties.backgroundColor": "#ffffff",
              "scalesProperties.lineColor": "#d4d4d4",
              "scalesProperties.textColor": "#525252",
              "scalesProperties.fontSize": 11,
              "paneProperties.legendProperties.showSeriesTitle": true,  // Ticker symbol: ON
              "paneProperties.legendProperties.showSeriesOHLC": true,
              "paneProperties.legendProperties.showBarChange": true,
              "paneProperties.legendProperties.showVolume": false,
              "scalesProperties.showStudyLastValue": false,
              "scalesProperties.showSeriesLastValue": true,  // Show current price label on axis
              
              // Candle colors - match theme (same for both themes)
              "mainSeriesProperties.candleStyle.upColor": "#10b981",
              "mainSeriesProperties.candleStyle.downColor": "#f6465d",
              "mainSeriesProperties.candleStyle.borderUpColor": "#10b981",
              "mainSeriesProperties.candleStyle.borderDownColor": "#f6465d",
              "mainSeriesProperties.candleStyle.wickUpColor": "#10b981",
              "mainSeriesProperties.candleStyle.wickDownColor": "#f6465d",
            });
          }
        }, 100);
      });
    }
  }, [theme, isReady]);

  // Handle modal TP/SL update
  const handleModalUpdate = (takeProfit: number | null, stopLoss: number | null) => {
    if (!userId || !modalPosition) return;

    updateTPSLMutation.mutate({
      user_id: userId,
      market: modalPosition.market,
      take_profit: takeProfit !== null ? takeProfit : undefined,
      stop_loss: stopLoss !== null ? stopLoss : undefined,
    });
  };
  
  // Suppress unused warning - modalPosition will be used when mobile modal is implemented
  if (modalPosition) console.log(modalPosition);

  return (
    <>
      {/* Loading State */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background">
          <LottieLoader size={80} message="Loading chart..." />
        </div>
      )}

      {/* Error State */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-background">
          <div className="text-center">
            <p className="text-sm text-destructive">Failed to load chart</p>
            <p className="text-xs text-muted-foreground mt-1">Please refresh the page</p>
          </div>
        </div>
      )}

      {/* Chart Container */}
      <div 
        ref={containerRef} 
        className="absolute inset-0"
        style={{ width: "100%", height: "100%", display: isLoading || hasError ? "none" : "block" }}
      />
      
      {/* Mobile TP/SL Edit Modal */}
      {isMobile && (
        <TPSLEditModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          position={modalPosition}
          onUpdate={handleModalUpdate}
        />
      )}
    </>
  );
};

